export enum BundleType {
  ONE_ON_ONE = 'one-on-one',
  TRIAL = 'trial',
}

/**
 * Session lengths a custom offer can be composed with — the single list behind
 * every offer-creation surface, so a duration one front door accepts is never
 * rejected by another.
 *
 * Lives here, not beside `BundleOfferBody`: that module imports class-validator
 * decorators, which need `reflect-metadata` at evaluation time and blow up in
 * the browser bundle. This file stays decorator-free so client components can
 * import the constant.
 */
export const OFFER_DURATION_OPTIONS = [30, 45, 50, 60, 90, 120];

/**
 * The price floor for every bundle — catalog products, trials and private
 * offers alike. $0 orders were removed 2026-08-05 (checkout always goes
 * through Stripe); this constant is the single owner of the minimum. Read it
 * in every validation and every piece of copy — never hardcode the number.
 */
export const MIN_BUNDLE_PRICE = 5;

/** Checkout's answer to a client buying a creator's catalogue intro a second
 *  time; the storefront says the same on the card before they can try. */
export const INTRO_ALREADY_BOUGHT = 'intro already bought';

/**
 * Every catalog trial is called this, in the DB and on the page: the owner does
 * not name it, and `BundleService.updateBundles` writes it whatever arrives.
 */
export const TRIAL_TITLE = 'Intro Call';

/**
 * What an untitled bundle is called: "Intro Call", "1 session", "4 sessions". Used
 * where a bundle has no title of its own — the default title at creation, and the
 * offer emails' heading. It was written twice, and the two spellings differed
 * ("1 session" vs "Your session"), which read oddly mid-sentence in the email:
 * "put together Your session for you".
 */
export function defaultBundleTitle(numberOfClasses: number, isTrial = false): string {
  if (isTrial) return TRIAL_TITLE;
  return numberOfClasses === 1 ? '1 session' : `${numberOfClasses} sessions`;
}

/**
 * The one way we describe a bundle's contents to a buyer: "1 one-on-one session ×
 * 60 min", "4 one-on-one sessions × 30 min". Used verbatim in the Stripe checkout
 * line item, the invoice, the purchase emails and the order-page summary — keep
 * them identical.
 *
 * It names the format because every session on this platform is a one-on-one call,
 * and a buyer often meets that fact here first — on an offer email or a checkout
 * line, before they have seen anything else. Note the reach: change this wording
 * and past invoices re-render with it, so an old invoice reads differently from the
 * Stripe receipt issued at the time.
 */
export function formatSessionPack(numberOfClasses: number, duration: number): string {
  return `${numberOfClasses} one-on-one session${numberOfClasses === 1 ? '' : 's'} × ${duration} min`;
}

/**
 * The full Stripe checkout line-item title, e.g.
 * "English pack (4 one-on-one sessions × 30 min) with anna". Single source for the
 * checkout item AND the invoice's product line — the invoice must read
 * identically to what the buyer saw at payment time.
 */
export function formatCheckoutItemTitle({
  title,
  numberOfClasses,
  duration,
  username,
}: {
  title: string | null;
  numberOfClasses: number;
  duration: number;
  username: string;
}): string {
  const pack = formatSessionPack(numberOfClasses, duration);
  const base = title ? `${title} (${pack})` : pack;
  return `${base} with ${username}`;
}
